import { useEffect, useState } from 'react';
import { ticketHistoryViewTypes } from '@twiliointernal/services-common/containers/TicketHistoryView';

import { SupportApi } from '@twiliointernal/e2-commons-shared-api';
import {
  GetTicketQueryParams,
  SupportApiResponse,
  TicketHistoryResponse,
} from '@twiliointernal/e2-commons-shared-definitions';
import { useSegment } from '@twiliointernal/e2-commons-shared-hooks';
import { logger } from '@twiliointernal/e2-commons-shared-libs';

import { constants, types } from './definitions';
import useUserDetails from '../useUserDetails';
import { trackingDetails } from '../../definitions/constants';

const useTickets = (): types.UseTicketsResults => {
  const {
    account,
    user,
    isLoggedIn,
    isLoading: isLoadingUser,
  } = useUserDetails();
  const [appliedQueryParams, updateAppliedQueryParams] =
    useState<GetTicketQueryParams>(constants.defaultQueryParams);
  const [currentPage, setCurrentPage] = useState<number>(
    constants.DEFAULT_FIRST_PAGE,
  );
  const [emptyState, setEmptyState] = useState<
    ticketHistoryViewTypes.TicketHistoryEmptyStateVariants | undefined
  >(undefined);
  const [ticketHistoryPermissions, setTicketHistoryPermissions] =
    useState<ticketHistoryViewTypes.TicketHistoryPermissions>({});
  const [tickets, setTickets] = useState<ticketHistoryViewTypes.Ticket[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [hasAccess, setHasAccess] = useState<boolean>(true);
  const [isError, setIsError] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const { trackEvent } = useSegment({
    accountSid: account?.sid,
    userSid: user?.sid,
    isLoggedIn,
    isLoading: isLoadingUser,
  });

  const getTickets = async (
    queryParams: GetTicketQueryParams,
  ): Promise<SupportApiResponse<TicketHistoryResponse>> => {
    const response = await SupportApi.getTickets(queryParams);

    const { success, message, statusCode } = response;

    if (!success) {
      throw new Error(`${statusCode}:${message}`);
    }

    return response;
  };

  useEffect(() => {
    if (!isLoadingUser) {
      (async (): Promise<void> => {
        try {
          const response = await getTickets(appliedQueryParams);
          const { count, results, metadata } = response?.data;

          const { canViewAccountLevelTicketHistory } = metadata;
          const permissions = { canViewAccountLevelTicketHistory };

          if (count === 0) {
            setEmptyState(
              ticketHistoryViewTypes.TicketHistoryEmptyStateVariants.noData,
            );
          }

          setTicketHistoryPermissions(permissions);
          setTotalCount(count);
          handleSetTickets(results);

          logger.info(`${constants.LOG_PREFIX}::[useEffect]`, {
            permissions,
            tickets: results,
            ticketsCount: count,
          });
        } catch (err) {
          logger.error(`${constants.LOG_PREFIX}::[useEffect]::${err}`);
          handleError(err, true);
        }
      })();
    }
  }, [isLoadingUser]);

  const handleSetTickets = (
    newTickets: ticketHistoryViewTypes.Ticket[],
  ): void => {
    setTickets(newTickets);
    setLoading(false);
  };

  const handleError = (error: Error, isInitialLoad?: boolean): void => {
    const [statusCode] = error.message.split(':');
    const hasAccessError = !!statusCode && statusCode === '403';

    if (isInitialLoad && !hasAccessError) {
      setEmptyState(
        ticketHistoryViewTypes.TicketHistoryEmptyStateVariants.initialLoadError,
      );
    }
    setHasAccess(!hasAccessError);
    setIsError(true);
    setLoading(false);
  };

  const handlePaginate = async (page: number): Promise<void> => {
    const previousPage = currentPage;

    try {
      setCurrentPage(page);
      setLoading(true);

      const newQueryParams: GetTicketQueryParams = {
        ...appliedQueryParams,
        paginate: {
          ...appliedQueryParams.paginate,
          page,
        },
      };

      updateAppliedQueryParams(newQueryParams);

      const { data } = await getTickets(newQueryParams);

      logger.info(`${constants.LOG_PREFIX}::[handlePaginate]`, {
        tickets: data.results,
        ticketsCount: data.count,
      });

      handleSetTickets(data.results);
    } catch (err) {
      logger.error(`${constants.LOG_PREFIX}::[handlePaginate]::${err}`);
      setCurrentPage(previousPage);
      handleError(err);
    }
  };

  const handleFilter = async (
    filters: ticketHistoryViewTypes.Filters | types.EmptyFilters,
  ): Promise<void> => {
    try {
      setLoading(true);
      setEmptyState(undefined);
      setCurrentPage(constants.DEFAULT_FIRST_PAGE);

      const eventName = trackingDetails.ticketHistoryFilterApplied;

      const { scope } = filters;

      const cleanedFilters = constants.VALID_SEARCH_FILTERS.reduce(
        (accumulator, filter) =>
          filters[filter] !== 'all'
            ? { ...accumulator, [filter]: filters[filter] }
            : accumulator,
        {},
      );

      const newQueryParams: GetTicketQueryParams = {
        ...appliedQueryParams,
        filter: cleanedFilters,
        paginate: {
          ...appliedQueryParams.paginate,
          page: constants.DEFAULT_FIRST_PAGE,
        },
        scope,
      };

      updateAppliedQueryParams(newQueryParams);

      trackEvent(eventName, { filters: cleanedFilters });

      const { data } = await getTickets(newQueryParams);

      logger.info(`${constants.LOG_PREFIX}::[handleFilter]::${eventName}`, {
        filters: cleanedFilters,
        ticketsCount: data.count,
      });

      if (data.count === 0) {
        setEmptyState(
          ticketHistoryViewTypes.TicketHistoryEmptyStateVariants.noResults,
        );
      }

      setTotalCount(data.count);
      handleSetTickets(data.results);
    } catch (err) {
      logger.error(`${constants.LOG_PREFIX}::[handleFilter]::${err}`);
      setTickets([]);
      setEmptyState(
        ticketHistoryViewTypes.TicketHistoryEmptyStateVariants.searchError,
      );
      handleError(err);
    }
  };

  return {
    appliedQueryParams,
    currentPage,
    emptyState,
    handleFilter,
    handlePaginate,
    hasAccess,
    isError,
    loading,
    ticketHistoryPermissions,
    tickets,
    totalCount,
  };
};

export default useTickets;
